import React, { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { X, Save } from "lucide-react";

// Dialog to edit a content note (per-message) or a group note. The
// `editing` prop carries either { id, note, ... } for a single message or
// { groupId, note, ... } for a whole group.
export function NoteDialog({ editing, onClose, onSave, onSaveGroup }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setText(editing?.note || "");
  }, [editing?.id, editing?.groupId, editing?.note]);

  if (!editing) return null;

  const isGroup = !!editing.groupId;

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isGroup) await onSaveGroup(editing.groupId, text);
      else await onSave(editing.id, text);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!editing} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="prod-note-dialog"
        className="max-w-lg rounded-3xl"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {isGroup ? "Nota del gruppo" : "Nota sul contenuto"}
          </DialogTitle>
          <DialogDescription>
            {editing.clientName ? `${editing.clientName} · ` : ""}
            {editing.fileName}
            {isGroup && " · la nota si applica a tutti i contenuti del gruppo"}
          </DialogDescription>
        </DialogHeader>
        <Textarea
          data-testid="prod-note-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Es: Caption approvata, da pubblicare venerdì alle 18:00. Hashtag già in prima comment…"
          className="min-h-[160px] rounded-2xl border-[var(--mp-border)]"
          autoFocus
        />
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-full border-[var(--mp-ink)]"
            data-testid="prod-note-cancel"
          >
            <X className="h-4 w-4 mr-1" /> Annulla
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="prod-note-save"
            className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
          >
            <Save className="h-4 w-4 mr-1" />
            {saving ? "Salvataggio…" : "Salva nota"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
